// 레이드 클리어 보상 연산(RAID-PRD 6절): 영수증 id 씨앗으로 세트 부위·옵션·전설을 굴려 장비 장부 연산 목록을 만든다(순수·결정적).
// 같은 영수증·영웅이면 언제 다시 만들어도 같은 연산(같은 id)이 나와 장부가 두 번 넣지 않는다 — 클리어 직후 지급과 재접속 정산이 같은 함수를 쓴다.
import { equipDef, rollGear, rolledItem, type GearItem } from '../gear';
import type { GearOp } from '../gearLedger';
import { EQUIPS } from './items';
import { stoneTierForIlvl, type StoneTier } from './economy';
import { raidDef, raidFirstId, raidHardDef, raidOpId, raidReceiptId, seededRand, type RaidDiff, type RaidId } from './raid';
import type { HeroId } from './types';

export interface RaidReward {
  readonly ops: GearOp[];
  /** 받은 세트 부위 정의 id와 전설 정의 id('' = 없음) */
  readonly set: string;
  readonly legend: string;
  readonly stones: number;
  readonly stoneTier: StoneTier;
  readonly protect: number;
  readonly gold: number;
}

/** 강화석 대신 줄 보호 주문서 몫(지역6 전: 보호 주문서를 쓸 수 없어 강화석으로 바꿔 준다, 일일 탑 규칙과 같다) */
export const PROTECT_AS_STONES = 10;

/**
 * week·n번째 클리어(1..weeklyLimit)의 보상. hero = 그 판 영웅(전설 무기 정의), protectOk = 보호 주문서 해금(지역6 클리어).
 * goldMult = 장비 골드 옵션(입장 때 고정 능력치)
 */
export function raidReward(raid: RaidId, week: number, n: number, hero: HeroId, protectOk: boolean, goldMult = 1, diff: RaidDiff = 'normal'): RaidReward {
  const def = raidDef(raid), loot = def.loot, id = raidReceiptId(raid, week, n, diff);
  // 하드(C13): 같은 표를 하드 배율로(골드·강화석 개수·전설 확률·세트 아이템 레벨·강화석 등급 = 하드 권장 레벨). 노말은 예전과 바이트까지 같다
  const hard = diff === 'hard' ? raidHardDef(raid) : null;
  const rand = seededRand(id);
  const ops: GearOp[] = [];
  // 세트 부위: 레이드 세트 중 하나(지금은 비룡 사냥꾼) → 4부위 중 하나. 세트 등급, 아이템 레벨 = 정의 레벨(권장 레벨)
  const setId = loot.sets[Math.min(loot.sets.length - 1, Math.floor(rand() * loot.sets.length))];
  const pieces = EQUIPS.filter((e) => e.setId === setId);
  const piece = pieces[Math.min(pieces.length - 1, Math.floor(rand() * pieces.length))];
  const setItem: GearItem = rolledItem(rand, piece, 'set', hard ? Math.max(piece.level, hard.loot.ilvl) : piece.level, raidOpId(raid, week, n, 'set', diff));
  ops.push({ id: raidOpId(raid, week, n, 'set', diff), t: 'pickup', item: setItem });
  // 전설: legendChance. 권장 레벨이 속한 지역의 지역보스 표에서 전설로 고정해 굴린다(그 지역 베이스·고유 효과)
  let legend = '';
  if (rand() < (hard ? hard.loot.legendChance : loot.legendChance)) {
    const region = piece.region;
    const item = rollGear(rand, { region, diff: hard ? 'hard' : 'normal', table: 'drop_final', hero }, 'boss', raidOpId(raid, week, n, 'legend', diff), 0, { min: 'legend', max: 'legend' });
    ops.push({ id: raidOpId(raid, week, n, 'legend', diff), t: 'pickup', item });
    legend = item.def;
  }
  // 강화석: 권장 레벨 아이템 레벨 등급. 보호 주문서는 이 레이드의 주 첫 클리어에만(지역6 전에는 강화석으로)
  const tier = stoneTierForIlvl(hard ? hard.recLevel : def.recLevel);
  const protect = n === 1 && loot.protectFirstClear > 0 && protectOk ? loot.protectFirstClear : 0;
  const stones = Math.round(loot.stones * (hard ? hard.loot.stonesMult : 1)) + (n === 1 && loot.protectFirstClear > 0 && !protectOk ? PROTECT_AS_STONES : 0);
  ops.push({ id: raidOpId(raid, week, n, 'stones', diff), t: 'stones', tier, n: stones });
  if (protect > 0) ops.push({ id: raidOpId(raid, week, n, 'protect', diff), t: 'protect', n: protect });
  return { ops, set: piece.id, legend, stones, stoneTier: tier, protect, gold: Math.round(loot.gold * (hard ? hard.loot.goldMult : 1) * goldMult) };
}

/**
 * 하드 첫 클리어 보상(고정 영수증 raidFirstId, 영원히 한 번 — C13): 확정 전설 1(하드 지역보스 표) + 골드 + 강화석. 칭호는 영수증이 있으면 보인다
 * (능력치 없음). 주 보상과 따로 받는다(주 보상이 다 찬 연습 판이어도 첫 클리어면 받는다). 하드가 없는 레이드면 null
 */
export function raidFirstReward(raid: RaidId, hero: HeroId, goldMult = 1): RaidReward | null {
  const hard = raidHardDef(raid);
  if (!hard) return null;
  const id = raidFirstId(raid, 'hard'), rand = seededRand(id);
  const piece = EQUIPS.find((e) => e.setId === raidDef(raid).loot.sets[0])!;
  const item = rollGear(rand, { region: piece.region, diff: 'hard', table: 'drop_final', hero }, 'boss', id + ':legend', 0, { min: 'legend', max: 'legend' });
  const tier = stoneTierForIlvl(hard.recLevel);
  const ops: GearOp[] = [
    { id: id + ':legend', t: 'pickup', item },
    { id: id + ':stones', t: 'stones', tier, n: hard.first.stones },
  ];
  return { ops, set: '', legend: item.def, stones: hard.first.stones, stoneTier: tier, protect: 0, gold: Math.round(hard.first.gold * goldMult) };
}

/** 장비 정의가 레이드 세트인지(결과 화면·아이콘) */
export function isRaidSetDef(id: string): boolean {
  const d = equipDef(id);
  return !!d && !!d.setId && (d.setId === 'set_wyvern' || d.setId === 'set_dragon');
}
